import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight, Phone, Mail, MapPin, CheckCircle, Star, Code, Smartphone, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { portfolioProjects, services, testimonials, contactInfo } from '../mock';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      
      if (response.data.success) {
        toast({
          title: "Bericht verzonden!",
          description: response.data.message,
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Fout",
        description: "Er ging iets mis. Probeer het opnieuw of bel direct.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsApp = () => {
    window.open(`https://wa.me/${contactInfo.whatsapp}?text=Hallo%20Thybe,%20ik%20ben%20geïnteresseerd%20in%20een%20website`, '_blank');
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-b border-zinc-800">
        <div className="container mx-auto px-4 md:px-6 py-3 md:py-4">
          <div className="flex items-center justify-between">
            <div className="text-lg md:text-2xl font-bold">
              <span className="text-white">AMPR</span>
              <span className="text-[#B8FF57]"> Consultancy</span>
            </div>
            <nav className="hidden md:flex items-center gap-8">
              <a href="#home" className="text-sm text-zinc-400 hover:text-white transition-colors">Home</a>
              <a href="#diensten" className="text-sm text-zinc-400 hover:text-white transition-colors">Diensten</a>
              <a href="#portfolio" className="text-sm text-zinc-400 hover:text-white transition-colors">Portfolio</a>
              <a href="#prijzen" className="text-sm text-zinc-400 hover:text-white transition-colors">Prijzen</a>
              <a href="#contact" className="text-sm text-zinc-400 hover:text-white transition-colors">Contact</a>
            </nav>
            <Button onClick={handleWhatsApp} className="bg-[#B8FF57] text-black hover:bg-[#a3e84a] font-semibold text-xs md:text-sm px-3 md:px-4 py-2">
              Vraag een website aan
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="pt-32 pb-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-8">
            <div className="inline-block">
              <span className="text-[#B8FF57] text-xs md:text-sm font-semibold tracking-wider uppercase">Webdesigner & Developer</span>
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-8xl font-bold leading-tight">
              WEBSITES DIE <br />
              <span className="text-[#B8FF57]">KLANTEN</span> OPLEVEREN
            </h1>
            <p className="text-base md:text-xl text-zinc-400 max-w-2xl mx-auto px-4">
              Ik bouw moderne websites voor bedrijven die online professioneel willen overkomen en meer klanten willen aantrekken.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6 px-4">
              <Button onClick={handleWhatsApp} size="lg" className="bg-[#B8FF57] text-black hover:bg-[#a3e84a] font-bold text-base md:text-lg px-6 md:px-8 py-5 md:py-6 group w-full sm:w-auto">
                Vraag een website aan
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button size="lg" variant="outline" className="border-zinc-700 text-white hover:bg-zinc-900 font-semibold text-base md:text-lg px-6 md:px-8 py-5 md:py-6 w-full sm:w-auto" onClick={() => document.getElementById('portfolio').scrollIntoView({ behavior: 'smooth' })}>
                Bekijk mijn werk
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="over" className="py-20 px-6 bg-zinc-950">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="flex justify-center order-2 md:order-1">
              <div className="relative">
                <div className="w-72 h-72 md:w-96 md:h-96 rounded-full overflow-hidden border-4 border-[#B8FF57] shadow-2xl mx-auto">
                  <img 
                    src="/thybe-photo.jpg" 
                    alt="Thybe Ampe" 
                    className="w-full h-full object-cover object-[center_8%] scale-125"
                  />
                </div>
                <div className="absolute -bottom-4 md:-bottom-6 -right-4 md:-right-6 bg-[#B8FF57] text-black px-4 py-2 md:px-6 md:py-3 rounded-lg font-bold text-base md:text-lg shadow-lg">
                  19 jaar
                </div>
              </div>
            </div>
            <div className="space-y-6 order-1 md:order-2">
              <h2 className="text-4xl md:text-5xl font-bold">
                OVER <span className="text-[#B8FF57]">MIJ</span>
              </h2>
              <p className="text-lg text-zinc-400 leading-relaxed">
                Ik ben <span className="text-white font-semibold">Thybe Ampe</span>, 19 jaar en webdesigner.
              </p>
              <p className="text-lg text-zinc-400 leading-relaxed">
                Ik help bedrijven met het bouwen van moderne, snelle en professionele websites die vertrouwen uitstralen en nieuwe klanten aantrekken.
              </p>
              <div className="grid grid-cols-1 gap-4 pt-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-1" size={20} />
                  <span className="text-zinc-300">Duidelijke structuur</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-1" size={20} />
                  <span className="text-zinc-300">Professioneel design</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-1" size={20} />
                  <span className="text-zinc-300">Gebruiksvriendelijke websites</span>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-1" size={20} />
                  <span className="text-zinc-300">Websites die klanten opleveren</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="diensten" className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-5xl font-bold">
              MIJN <span className="text-[#B8FF57]">DIENSTEN</span>
            </h2>
            <p className="text-xl text-zinc-400">Alles wat je nodig hebt voor een succesvolle online aanwezigheid</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service) => (
              <Card key={service.id} className="bg-zinc-900 border-zinc-800 hover:border-[#B8FF57] transition-all duration-300 group">
                <CardContent className="p-8 space-y-4">
                  <div className="w-12 h-12 bg-[#B8FF57]/10 rounded-lg flex items-center justify-center group-hover:bg-[#B8FF57]/20 transition-colors">
                    {service.id === 1 && <Code className="text-[#B8FF57]" size={24} />}
                    {service.id === 2 && <Zap className="text-[#B8FF57]" size={24} />}
                    {service.id === 3 && <Smartphone className="text-[#B8FF57]" size={24} />}
                  </div>
                  <h3 className="text-2xl font-bold text-white">{service.title}</h3>
                  <p className="text-zinc-400">{service.description}</p>
                  <ul className="space-y-2 pt-4">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-zinc-400">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#B8FF57]"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 px-6 bg-zinc-950">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl md:text-5xl font-bold">
              MIJN <span className="text-[#B8FF57]">PORTFOLIO</span>
            </h2>
            <p className="text-lg md:text-xl text-zinc-400">Recent gerealiseerde projecten voor lokale bedrijven</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {portfolioProjects.map((project) => (
              <Card key={project.id} className="bg-zinc-900 border-zinc-800 overflow-hidden group hover:border-[#B8FF57] transition-all duration-300">
                <div className="relative overflow-hidden h-80">
                  <img 
                    src={project.image} 
                    alt={project.title} 
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-60 group-hover:opacity-80 transition-opacity duration-300"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-0 transition-transform duration-300">
                    <div className="text-xs font-semibold text-[#B8FF57] mb-2">{project.category}</div>
                    <h3 className="text-xl font-bold text-white mb-2">{project.title}</h3>
                    <p className="text-zinc-300 text-sm">{project.description}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="prijzen" className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
              TRANSPARANTE <span className="text-[#B8FF57]">PRIJZEN</span>
            </h2>
            <p className="text-base md:text-lg lg:text-xl text-zinc-400">Kies het pakket dat bij jou past</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Starter Package */}
            <Card className="bg-zinc-900 border-zinc-800 hover:border-[#B8FF57] transition-all duration-300">
              <CardContent className="p-8 space-y-6">
                <div>
                  <h3 className="text-3xl font-bold mb-2 text-white">Starter Website</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-[#B8FF57]">€650</span>
                    <span className="text-zinc-400">- €1200</span>
                  </div>
                  <p className="text-sm text-zinc-400 mt-2">Eenmalige investering</p>
                </div>
                <div className="space-y-3">
                  <p className="text-zinc-300">Ideaal voor startende bedrijven</p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-sm text-zinc-400">Professioneel design op maat</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-sm text-zinc-400">Mobielvriendelijke website</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-sm text-zinc-400">Contactformulier</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-sm text-zinc-400">Basis SEO optimalisatie</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-sm text-zinc-400">Snelle laadtijd</span>
                    </div>
                  </div>
                </div>
                <div className="pt-4 border-t border-zinc-800">
                  <div className="bg-zinc-800 p-4 rounded-lg">
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="text-2xl font-bold text-[#B8FF57]">€45</span>
                      <span className="text-zinc-400">per maand</span>
                    </div>
                    <p className="text-xs text-zinc-400">Voor onderhoud, updates, aanpassingen, hosting en support</p>
                  </div>
                </div>
                <Button onClick={handleWhatsApp} className="w-full bg-[#B8FF57] text-black hover:bg-[#a3e84a] font-bold py-6">
                  Kies dit pakket
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </CardContent>
            </Card>

            {/* Subscription Package */}
            <Card className="bg-gradient-to-br from-[#B8FF57]/10 to-zinc-900 border-[#B8FF57] relative overflow-hidden">
              <div className="absolute top-4 right-4 bg-[#B8FF57] text-black text-xs font-bold px-3 py-1 rounded-full">
                POPULAIR
              </div>
              <CardContent className="p-8 space-y-6">
                <div>
                  <h3 className="text-3xl font-bold mb-2 text-white">Website Abonnement</h3>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-[#B8FF57]">€175</span>
                    <span className="text-zinc-400">per maand</span>
                  </div>
                  <p className="text-sm text-zinc-400 mt-2">Geen grote startkost</p>
                </div>
                <div className="space-y-3">
                  <p className="text-zinc-300">Ideaal voor groeiende bedrijven</p>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-sm text-zinc-400">Professionele website op maat</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-sm text-zinc-400">Regelmatige updates & onderhoud</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-sm text-zinc-400">Onbeperkte aanpassingen</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-sm text-zinc-400">Hosting & technische support</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-sm text-zinc-400">Priority support</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle className="text-[#B8FF57] flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-sm text-zinc-400">Maandelijks opzegbaar</span>
                    </div>
                  </div>
                </div>
                <Button onClick={handleWhatsApp} className="w-full bg-[#B8FF57] text-black hover:bg-[#a3e84a] font-bold py-6 mt-auto">
                  Kies dit pakket
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Professional Website */}
      <section id="waarom" className="py-20 px-6 bg-zinc-950">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold leading-tight px-4">
              WAAROM EEN PROFESSIONELE <span className="text-[#B8FF57]">WEBSITE?</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="bg-zinc-900 border-zinc-800 text-center">
              <CardContent className="p-8 space-y-4">
                <div className="w-16 h-16 bg-[#B8FF57]/10 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle className="text-[#B8FF57]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white">Meer Vertrouwen</h3>
                <p className="text-zinc-400">Professionele uitstraling wekt vertrouwen bij potentiële klanten</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 text-center">
              <CardContent className="p-8 space-y-4">
                <div className="w-16 h-16 bg-[#B8FF57]/10 rounded-full flex items-center justify-center mx-auto">
                  <Zap className="text-[#B8FF57]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white">Beter Vindbaar</h3>
                <p className="text-zinc-400">SEO geoptimaliseerd zodat klanten je gemakkelijk kunnen vinden</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 text-center">
              <CardContent className="p-8 space-y-4">
                <div className="w-16 h-16 bg-[#B8FF57]/10 rounded-full flex items-center justify-center mx-auto">
                  <Star className="text-[#B8FF57]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white">Professionele Uitstraling</h3>
                <p className="text-zinc-400">Val op tussen de concurrentie met een unieke website</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 text-center">
              <CardContent className="p-8 space-y-4">
                <div className="w-16 h-16 bg-[#B8FF57]/10 rounded-full flex items-center justify-center mx-auto">
                  <ArrowRight className="text-[#B8FF57]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-white">Meer Klanten</h3>
                <p className="text-zinc-400">Genereer meer aanvragen en vergroot je klantenbestand</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-5xl font-bold">
              WAT <span className="text-[#B8FF57]">KLANTEN</span> ZEGGEN
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-8 space-y-4">
                  <div className="flex gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="text-[#B8FF57] fill-[#B8FF57]" size={16} />
                    ))}
                  </div>
                  <p className="text-zinc-300 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-bold text-white">{testimonial.name}</p>
                    <p className="text-sm text-zinc-400">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-6 bg-zinc-950">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-5xl font-bold">
              VRAAG VANDAAG NOG <span className="text-[#B8FF57]">JOUW WEBSITE</span> AAN
            </h2>
            <p className="text-xl text-zinc-400">Neem contact op en laten we samen bouwen aan jouw online succes</p>
          </div>
          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-white">Naam *</label>
                    <Input 
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Je naam"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-white">Email *</label>
                    <Input 
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="je@email.com"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-white">Telefoon</label>
                    <Input 
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      placeholder="0493 97 33 43"
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-white">Bericht *</label>
                    <Textarea 
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      placeholder="Vertel me over je project..."
                      rows={5}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                  <Button type="submit" disabled={isSubmitting} className="w-full bg-[#B8FF57] text-black hover:bg-[#a3e84a] font-bold py-6">
                    {isSubmitting ? 'Versturen...' : 'Verstuur bericht'}
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Contact Info */}
            <div className="space-y-8">
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#B8FF57]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="text-[#B8FF57]" size={20} />
                    </div>
                    <div>
                      <p className="font-semibold mb-1 text-white">Telefoon</p>
                      <a href={`tel:${contactInfo.phone}`} className="text-zinc-400 hover:text-[#B8FF57] transition-colors">
                        {contactInfo.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#B8FF57]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="text-[#B8FF57]" size={20} />
                    </div>
                    <div>
                      <p className="font-semibold mb-1 text-white">Email</p>
                      <a href={`mailto:${contactInfo.email}`} className="text-zinc-400 hover:text-[#B8FF57] transition-colors break-all">
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#B8FF57]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-[#B8FF57]" size={20} />
                    </div>
                    <div>
                      <p className="font-semibold mb-1 text-white">Adres</p>
                      <p className="text-zinc-400">{contactInfo.address}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-[#B8FF57] to-[#a3e84a] border-0">
                <CardContent className="p-8 text-center space-y-4">
                  <h3 className="text-2xl font-bold text-black">Direct Contact via WhatsApp</h3>
                  <p className="text-black/80">Krijg binnen 24 uur een persoonlijk antwoord</p>
                  <Button 
                    onClick={handleWhatsApp}
                    className="bg-black text-[#B8FF57] hover:bg-zinc-900 font-bold w-full py-6"
                  >
                    <Phone className="mr-2" size={18} />
                    Open WhatsApp
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black border-t border-zinc-800 py-12 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="text-2xl font-bold mb-4">
                <span className="text-white">AMPR</span>
                <span className="text-[#B8FF57]"> Consultancy</span>
              </div>
              <p className="text-zinc-400 text-sm">
                Professionele websites die klanten opleveren
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-[#B8FF57]">Navigatie</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#home" className="text-zinc-400 hover:text-white transition-colors">Home</a></li>
                <li><a href="#diensten" className="text-zinc-400 hover:text-white transition-colors">Diensten</a></li>
                <li><a href="#portfolio" className="text-zinc-400 hover:text-white transition-colors">Portfolio</a></li>
                <li><a href="#prijzen" className="text-zinc-400 hover:text-white transition-colors">Prijzen</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-[#B8FF57]">Contact</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li>{contactInfo.phone}</li>
                <li>{contactInfo.email}</li>
                <li>{contactInfo.address}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4 text-[#B8FF57]">Bedrijfsinfo</h4>
              <ul className="space-y-2 text-sm text-zinc-400">
                <li>BTW: {contactInfo.vat}</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-zinc-800 pt-8 text-center text-sm text-zinc-400">
            <p>© {new Date().getFullYear()} Thybe Ampe. Alle rechten voorbehouden.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
